import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
// DEĞİŞİKLİK: Send ikonu eklendi
import { Calendar, MessageSquare, BookOpen, UserPlus, X, Heart, MessageCircle, Trash2, Book, MoreHorizontal, Send } from 'lucide-react';
import { FaHeart, FaRegHeart } from 'react-icons/fa';
import api from '../services/api';
import getProfileImageUrl from '../utils/getProfileImageUrl';
import '../styles/Community.css';

// Zamanı formatlamak için yardımcı fonksiyon
const timeAgo = (date) => {
    const seconds = Math.floor((new Date() - new Date(date)) / 1000);
    let interval = seconds / 31536000;
    if (interval > 1) return Math.floor(interval) + " yıl önce";
    interval = seconds / 2592000;
    if (interval > 1) return Math.floor(interval) + " ay önce";
    interval = seconds / 86400;
    if (interval > 1) return Math.floor(interval) + " gün önce";
    interval = seconds / 3600;
    if (interval > 1) return Math.floor(interval) + " saat önce";
    interval = seconds / 60;
    if (interval > 1) return Math.floor(interval) + " dakika önce";
    return "az önce";
};

function Community() {
    const { profileUserId } = useParams();
    const navigate = useNavigate();
    const currentUserId = localStorage.getItem('userId');
    const token = localStorage.getItem('token');
    const dropdownRef = useRef(null);

    // State değişkenleri
    const [user, setUser] = useState(null);
    const [currentUser, setCurrentUser] = useState(null);
    const [isFollowing, setIsFollowing] = useState(false);
    const [isCreatePostModalOpen, setCreatePostModalOpen] = useState(false);
    const [newPostContent, setNewPostContent] = useState('');
    const [communityPosts, setCommunityPosts] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [confirmationModal, setConfirmationModal] = useState({
        isOpen: false,
        message: '',
        onConfirm: null,
    });
    const [activeDropdown, setActiveDropdown] = useState(null);

    // Dışarıya tıklandığında menüleri kapatmak için
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target) && !event.target.closest('.options-btn')) {
                setActiveDropdown(null);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Sayfa verilerini çekmek için
    useEffect(() => {
        if (!profileUserId || !token) {
            navigate('/login');
            return;
        }

        const fetchData = async () => {
            setIsLoading(true);
            try {
                // Profil sahibinin ve mevcut kullanıcının verilerini aynı anda çek
                const [profileRes, currentUserRes] = await Promise.all([
                    api.get(`/profile/${profileUserId}`, { headers: { Authorization: `Bearer ${token}` } }),
                    api.get(`/profile/${currentUserId}`, { headers: { Authorization: `Bearer ${token}` } })
                ]);
                
                // Profil verilerini ayarla
                const { user: userData, books: userBooks, isFollowing: followStatus } = profileRes.data;
                setUser({
                    ...userData,
                    handle: `@${userData.username}`,
                    joinDate: new Date(userData.join_date).toLocaleDateString('tr-TR', { year: 'numeric', month: 'long' }),
                    profilePhoto: getProfileImageUrl(userData.profile_image_url),
                    readingList: userBooks ? userBooks.length : 0,
                });
                setIsFollowing(followStatus);
                
                // Mevcut kullanıcı verilerini ayarla
                const currentUserData = currentUserRes.data.user;
                setCurrentUser({
                    username: currentUserData.username,
                    profilePhoto: getProfileImageUrl(currentUserData.profile_image_url),
                });
                
                // Gönderileri çek
                const postsRes = await api.get(`/community/user/${profileUserId}/posts`, { headers: { Authorization: `Bearer ${token}` } });
                const postsWithComments = await Promise.all(
                    postsRes.data.map(async (post) => {
                        try {
                            const commentsResponse = await api.get(`/community/posts/${post.post_id}/comments`, { headers: { Authorization: `Bearer ${token}` } });
                            return {
                                ...post,
                                profile_image_url: getProfileImageUrl(post.profile_image_url),
                                comments: commentsResponse.data.map(comment => ({
                                    ...comment,
                                    isLiked: comment.is_liked,
                                    profile_image_url: getProfileImageUrl(comment.profile_image_url),
                                })),
                                newComment: '',
                                showComments: false,
                                isLiked: post.is_liked,
                            };
                        } catch (commentError) {
                            console.error(`Yorumlar alınırken hata oluştu (Post ID: ${post.post_id}):`, commentError);
                            return { ...post, comments: [], newComment: '', showComments: false, isLiked: post.is_liked };
                        }
                    })
                );
                setCommunityPosts(postsWithComments);

            } catch (error) {
                console.error('Topluluk verileri alınırken hata oluştu:', error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchData();
    }, [navigate, profileUserId, currentUserId, token]);

    // Takip etme/bırakma fonksiyonu
    const handleFollowClick = async () => {
        try {
            const url = isFollowing ? `/profile/unfollow/${profileUserId}` : `/profile/follow/${profileUserId}`;
            await api.post(url, {}, { headers: { Authorization: `Bearer ${token}` } });
            setIsFollowing(!isFollowing);
            setUser(prevUser => ({
                ...prevUser,
                followers_count: isFollowing ? prevUser.followers_count - 1 : prevUser.followers_count + 1,
            }));
        } catch (error) {
            console.error('Takip durumu güncellenirken hata:', error);
        }
    };

    // Beğenme/beğeniyi geri alma fonksiyonu
    const handleLike = async (postId, commentId = null) => {
        const url = commentId ? `/community/comments/${commentId}/like` : `/community/posts/${postId}/like`;
        try {
            await api.post(url, { userId: parseInt(currentUserId) }, { headers: { Authorization: `Bearer ${token}` } });
            setCommunityPosts(prevPosts =>
                prevPosts.map(post => {
                    if (post.post_id !== postId) return post;
                    if (commentId) {
                        return {
                            ...post,
                            comments: post.comments.map(comment =>
                                comment.comment_id === commentId ? { ...comment, isLiked: !comment.isLiked, likes_count: comment.isLiked ? comment.likes_count - 1 : comment.likes_count + 1 } : comment
                            )
                        };
                    } else {
                        return { ...post, isLiked: !post.isLiked, likes_count: post.isLiked ? post.likes_count - 1 : post.likes_count + 1 };
                    }
                })
            );
        } catch (error) {
            console.error('Beğenme işlemi sırasında hata:', error);
        }
    };

    // Yeni gönderi oluşturma fonksiyonu
    const handlePostSubmit = async (e) => {
        e.preventDefault();
        if (!newPostContent.trim()) return;
        try {
            const response = await api.post(`/community/posts`,
                { userId: parseInt(currentUserId), content: newPostContent },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            const newPost = {
                post_id: response.data.postId,
                user_id: parseInt(currentUserId),
                profile_image_url: currentUser.profilePhoto,
                username: currentUser.username,
                content: newPostContent,
                created_at: new Date().toISOString(),
                likes_count: 0,
                isLiked: false,
                comments: [],
                comments_count: 0,
                showComments: false,
                newComment: '',
            };
            setCommunityPosts([newPost, ...communityPosts]);
            setNewPostContent('');
            setCreatePostModalOpen(false);
        } catch (error) {
            console.error('Gönderi oluşturulurken hata:', error);
        }
    };

    // Yorum gönderme fonksiyonu
    const handleCommentSubmit = async (e, postId) => {
        e.preventDefault();
        const post = communityPosts.find(p => p.post_id === postId);
        if (!post || !post.newComment.trim()) return;

        try {
            const response = await api.post(`/community/posts/${postId}/comments`,
                {
                    userId: parseInt(currentUserId),
                    postId: postId,
                    content: post.newComment
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            const newComment = {
                comment_id: response.data.commentId,
                user_id: parseInt(currentUserId),
                profile_image_url: currentUser.profilePhoto,
                username: currentUser.username,
                content: post.newComment,
                created_at: new Date().toISOString(),
                likes_count: 0,
                isLiked: false,
            };

            setCommunityPosts(prevPosts =>
                prevPosts.map(p =>
                    p.post_id === postId ?
                    {
                        ...p,
                        comments: [...p.comments, newComment],
                        newComment: '',
                        comments_count: (p.comments_count || 0) + 1,
                    } :
                    p
                )
            );
        } catch (error) {
            console.error('Yorum eklenirken hata:', error.response ? error.response.data : error.message);
        }
    };
    
    // Yorum input alanını güncelleme
    const handleCommentChange = (postId, value) => {
        setCommunityPosts(prev =>
            prev.map(post =>
                post.post_id === postId ? { ...post, newComment: value } : post
            )
        );
    };

    // Yorumları gösterme/gizleme
    const toggleComments = (postId) => {
        setCommunityPosts(prev =>
            prev.map(post =>
                post.post_id === postId ? { ...post, showComments: !post.showComments } : post
            )
        );
    };

    // Silme onayı ve işlemleri...
    const handleDeleteRequest = (type, action) => {
        setConfirmationModal({ isOpen: true, message: `${type} silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.`, onConfirm: action });
    };
    const confirmDeletion = () => {
        if (confirmationModal.onConfirm) confirmationModal.onConfirm();
        setConfirmationModal({ isOpen: false, message: '', onConfirm: null });
        setActiveDropdown(null);
    };
    const handleDeletePost = (postId) => handleDeleteRequest('Gönderiyi', async () => {
        try {
            await api.delete(`/community/posts/${postId}`, { headers: { Authorization: `Bearer ${token}` } });
            setCommunityPosts(prev => prev.filter(p => p.post_id !== postId));
        } catch (error) { console.error('Gönderi silinirken hata:', error); }
    });
    const handleDeleteComment = (postId, commentId) => handleDeleteRequest('Yorumu', async () => {
        try {
            await api.delete(`/community/comments/${commentId}`, { headers: { Authorization: `Bearer ${token}` } });
            setCommunityPosts(prev => prev.map(post => post.post_id === postId ? { ...post, comments: post.comments.filter(c => c.comment_id !== commentId), comments_count: (post.comments_count || 1) - 1 } : post));
        } catch (error) { console.error('Yorum silinirken hata:', error); }
    });
    const toggleDropdown = (id) => {
        setActiveDropdown(activeDropdown === id ? null : id);
    };


    if (isLoading || !user || !currentUser) {
        return (
            <div className="community-loading-container">
                <div className="community-loading-spinner"></div>
            </div>
        );
    }

    const isOwnProfile = parseInt(profileUserId) === parseInt(currentUserId);

    // JSX render kısmı
    return (
        <div className="community-page">
            <header className="community-header">
                <div className="community-cover-photo"></div>
                <div className="community-header-bottom">
                    <div className="community-header-top">
                        <div className="community-avatar-container">
                            <img src={user.profilePhoto} alt={user.username} className="community-avatar" />
                        </div>
                        <div className="community-header-actions">
                             <button className="community-action-btn icon-only" onClick={() => navigate(`/profile/${profileUserId}`)} title="Profil Sayfası">
                                 <Book size={18} />
                             </button>
                            {isOwnProfile ? (
                                <button className="community-action-btn" onClick={() => navigate('/settings')}>
                                    Ayarları Düzenle
                                </button>
                            ) : (
                                <>
                                    <button className="community-action-btn icon-only" onClick={() => navigate(`/inbox?recipient_username=${user.username}`)} title="Mesaj Gönder">
                                        <MessageSquare size={18} />
                                    </button>
                                    <button className={`community-action-btn with-icon ${isFollowing ? 'secondary' : 'primary'}`} onClick={handleFollowClick}>
                                        <UserPlus size={18} />
                                        <span>{isFollowing ? 'Takibi Bırak' : 'Takip Et'}</span>
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                    <div className="community-user-info">
                        <h1 className="community-display-name">{user.full_name}</h1>
                        <p className="community-username">{user.handle}</p>
                        <p className="community-bio">{user.bio || "Henüz bir biyografi eklenmemiş."}</p>
                        <div className="community-meta-info">
                            <span><BookOpen size={16} /> {user.readingList} Kitap</span>
                            <span><Calendar size={16} /> {user.joinDate} tarihinde katıldı</span>
                        </div>
                        <div className="community-stats">
                            <span><strong>{user.following_count}</strong> Takip Edilen</span>
                            <span><strong>{user.followers_count}</strong> Takipçi</span>
                        </div>
                    </div>
                </div>
            </header>

            <main className="community-main">
                <div className="community-feed">
                    {isOwnProfile && (
                        <div className="create-post-prompt">
                            <img src={currentUser.profilePhoto} alt="avatar" className="prompt-avatar" />
                            <div className="prompt-input" onClick={() => setCreatePostModalOpen(true)}>Aklından ne geçiyor, {currentUser.username}?</div>
                            <button className="prompt-button" onClick={() => setCreatePostModalOpen(true)}>Paylaş</button>
                        </div>
                    )}
                    <div className="post-list">
                        {communityPosts.length > 0 ? communityPosts.map(post => (
                            <div key={post.post_id} className="post-card">
                                <div className="post-header">
                                    <Link to={`/community/${post.user_id}`}>
                                        <img src={post.profile_image_url} alt={post.username} className="post-avatar" />
                                    </Link>
                                    <div className="post-header-info">
                                        <Link to={`/community/${post.user_id}`} className="post-author">
                                            {post.username}
                                        </Link>
                                        <span className="post-handle">@{post.username}</span>
                                        <span className="post-timestamp">{timeAgo(post.created_at)}</span>
                                    </div>
                                    {parseInt(post.user_id) === parseInt(currentUserId) && (
                                        <div className="post-options" ref={dropdownRef}>
                                            <button className="options-btn" onClick={() => toggleDropdown(`post-${post.post_id}`)}>
                                                <MoreHorizontal size={20} />
                                            </button>
                                            {activeDropdown === `post-${post.post_id}` && (
                                                <div className="options-dropdown">
                                                    <button onClick={() => handleDeletePost(post.post_id)}>
                                                        <Trash2 size={16} /> Sil
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                                <div className="post-content">
                                    <p>{post.content}</p>
                                </div>
                                <div className="post-actions">
                                    <button className={`action-btn like-btn ${post.isLiked ? 'liked' : ''}`} onClick={() => handleLike(post.post_id)}>
                                        {post.isLiked ? <FaHeart size={18} /> : <FaRegHeart size={18} />}
                                        <span className="action-count">{post.likes_count || 0}</span>
                                    </button>
                                    <button className="action-btn comment-btn" onClick={() => toggleComments(post.post_id)}>
                                        <MessageCircle size={18} />
                                        <span className="action-count">{post.comments_count || post.comments.length}</span>
                                    </button>
                                </div>
                                {post.showComments && (
                                    <div className="comments-section">
                                        <div className="comment-form-wrapper">
                                            <form className="comment-form" onSubmit={(e) => handleCommentSubmit(e, post.post_id)}>
                                                <input
                                                    type="text"
                                                    placeholder="Yorumunuzu yazın..."
                                                    value={post.newComment}
                                                    onChange={(e) => handleCommentChange(post.post_id, e.target.value)}
                                                    className="comment-input"
                                                />
                                                {/* DEĞİŞİKLİK: Buton içeriği ikon ve span ile güncellendi */}
                                                <button type="submit" className="comment-submit-btn">
                                                    <Send size={16} />
                                                    <span>Gönder</span>
                                                </button>
                                            </form>
                                        </div>
                                        <div className="comment-list">
                                            {post.comments.map(comment => (
                                                <div key={comment.comment_id} className="comment-card">
                                                    <Link to={`/community/${comment.user_id}`}>
                                                        <img src={comment.profile_image_url} alt={comment.username} className="comment-avatar" />
                                                    </Link>
                                                    <div className="comment-body">
                                                        <div className="comment-header">
                                                            <div className="comment-info">
                                                                <Link to={`/community/${comment.user_id}`} className="comment-author">
                                                                    {comment.username}
                                                                </Link>
                                                                <span className="comment-handle">@{comment.username}</span>
                                                                <span className="comment-timestamp">{timeAgo(comment.created_at)}</span>
                                                            </div>
                                                            {parseInt(comment.user_id) === parseInt(currentUserId) && (
                                                                <div className="comment-options" ref={dropdownRef}>
                                                                    <button className="options-btn" onClick={() => toggleDropdown(`comment-${comment.comment_id}`)}>
                                                                        <MoreHorizontal size={16} />
                                                                    </button>
                                                                    {activeDropdown === `comment-${comment.comment_id}` && (
                                                                        <div className="options-dropdown">
                                                                            <button onClick={() => handleDeleteComment(post.post_id, comment.comment_id)}>
                                                                                <Trash2 size={14} /> Sil
                                                                            </button>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                        <p className="comment-content">{comment.content}</p>
                                                        <div className="comment-actions">
                                                            <button className={`action-btn like-btn ${comment.isLiked ? 'liked' : ''}`} onClick={() => handleLike(post.post_id, comment.comment_id)}>
                                                                {comment.isLiked ? <FaHeart size={16} /> : <FaRegHeart size={16} />}
                                                                <span className="action-count">{comment.likes_count || 0}</span>
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )) : (
                            <div className="no-posts-message">
                                <h3>Henüz gönderi yok</h3>
                                <p>{user.username} henüz bir şey paylaşmadı. Geri dönüp daha sonra tekrar kontrol edin.</p>
                            </div>
                        )}
                    </div>
                </div>
            </main>

            {/* Modallar */}
            {isCreatePostModalOpen && (
                <div className="modal-overlay" onClick={() => setCreatePostModalOpen(false)}>
                    <div className="modal-content" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Yeni Gönderi Oluştur</h3>
                            <button className="modal-close-btn" onClick={() => setCreatePostModalOpen(false)}><X/></button>
                        </div>
                        <form className="modal-form" onSubmit={handlePostSubmit}>
                            <textarea
                                placeholder={`Aklından ne geçiyor, ${currentUser.username}?`}
                                value={newPostContent}
                                onChange={(e) => setNewPostContent(e.target.value)}
                                autoFocus
                            />
                            <button type="submit" className="modal-submit-btn" disabled={!newPostContent.trim()}>Paylaş</button>
                        </form>
                    </div>
                </div>
            )}
            {confirmationModal.isOpen && (
                <div className="modal-overlay">
                    <div className="confirm-modal-content">
                        <h4>Onay Gerekiyor</h4>
                        <p>{confirmationModal.message}</p>
                        <div className="confirm-modal-actions">
                            <button className="confirm-btn cancel" onClick={() => setConfirmationModal({ isOpen: false, message: '', onConfirm: null })}>İptal</button>
                            <button className="confirm-btn delete" onClick={confirmDeletion}>Sil</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Community;